/* ------------------------------------------------------------------
   ms-challenge-planner.js
   ------------------------------------------------------------------
   Tab "Planung" der Challenge-Seiten: Startort, Stufe und (bei Silber)
   Etappen oder Basecamp waehlen, darunter der Plan mit Naechten,
   Tageskarten und Partnerhotels.

   Herkunft: Prototyp challenge-proto2/src/planner.js und dropdown.js,
   Etappenalgorithmus aus challenge-proto/build-data.mjs. Drei Dinge sind
   anders, mit Absicht:
     1. Die Etappen werden hier im Browser gerechnet, nicht vorab. Nur so
        wirkt ein neu angelegtes Hotel sofort, ohne dass jemand ein
        Skript anstoesst. Gerechnet wird in Shop-Kilometern (die
        Streckendaten sind schon skaliert), das ergibt dieselben Werte
        wie der Generator, der in GPX-Kilometern rechnete und skalierte.
     2. Hotels kommen aus den Metaobjekten (Liquid schreibt sie als JSON
        in die Section). Ihre Lage an der Runde: route_access aus dem
        Geo-Lauf, sonst planner_geo (Koordinaten aus dem Admin, auf die
        Linie projiziert), sonst der Ort.
     3. Die Auswahl schreibt in die Kaufbox (Radio der Stufe), nie in
        einen zweiten Zustand. Die Kaufbox schreibt per
        ms:selection-change zurueck.

   Ein Global: window.MSPlanner (reine Rechenfunktionen, auch in Node
   pruefbar). Das Modul haengt sich an window.MSChallenge.
   ------------------------------------------------------------------ */
(function () {
  'use strict';
  var WINDOW = 0.15;        // Fangbereich um den idealen Etappen-Endpunkt (Anteil der Runde)
  var HOTEL_SNAP_KM = 8;    // Hotel rastet ein, wenn es hoechstens so weit vom Ideal liegt
  var OVERNIGHT_M = 3000;   // Hotel zaehlt als Uebernachtung, wenn es hoechstens so weit vom Etappenende liegt
  var PASS_RUN_KM = 2.5;    // Durchfahrten: Punkte naeher als 3 km, Laeufe bis 2,5 km werden zu einer Durchfahrt
  var TIERS = { bronze: 3, silber: 2, gold: 1 };
  var TIER_KEYS = ['bronze', 'silber', 'gold'];

  /* ---------- Rechnen ---------- */
  function r1(v) { return Math.round(v * 10) / 10; }
  function roundTo(rows, key, step) {
    var raw = rows.map(function (r) { return r[key] / step; });
    var target = Math.round(raw.reduce(function (t, v) { return t + v; }, 0));
    var fl = raw.map(Math.floor);
    var rest = target - fl.reduce(function (t, v) { return t + v; }, 0);
    raw.map(function (v, i) { return [v - fl[i], i]; }).sort(function (a, b) { return b[0] - a[0]; })
      .forEach(function (x) { if (rest > 0) { fl[x[1]]++; rest--; } });
    rows.forEach(function (r, i) { r[key] = fl[i] * step; });
  }
  // Interpolation entlang einer nach km sortierten Liste; get(p) liefert km
  function interp(list, km, kmOf, valOf) {
    var lo = 0, hi = list.length - 1;
    if (km <= kmOf(list[0])) return valOf(list[0]);
    if (km >= kmOf(list[hi])) return valOf(list[hi]);
    while (hi - lo > 1) { var m = (lo + hi) >> 1; if (kmOf(list[m]) <= km) lo = m; else hi = m; }
    var a = list[lo], b = list[hi], span = kmOf(b) - kmOf(a), t = span > 0 ? (km - kmOf(a)) / span : 0;
    var va = valOf(a), vb = valOf(b);
    if (typeof va === 'number') return va + (vb - va) * t;
    return va.map(function (v, i) { return v + (vb[i] - v) * t; });
  }
  function slug(s) {
    return String(s || '').toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  /* Streckendaten (assets/ms-route-*.json) aufbereiten: Laenge, Punkt und Anstieg je km,
     Projektion fuer Koordinaten aus dem Admin, Entfernung in Metern aus Kartenpunkten */
  function prepRoute(D) {
    var pts = D.pts, prof = D.profile, PJ = D.meta.projection;
    var L = prof[prof.length - 1][0];
    var wrap = function (km) { return ((km % L) + L) % L; };
    var at = function (km) { return interp(pts, wrap(km), function (p) { return p[2]; }, function (p) { return [p[0], p[1]]; }); };
    var upAt = function (km) { return interp(prof, km, function (p) { return p[0]; }, function (p) { return p[4]; }); };
    // Anstieg von a ueber len km, mit Umbruch am Ende der Runde
    var gain = function (a, len) { var b = a + len; return b <= L ? upAt(b) - upAt(a) : (upAt(L) - upAt(a)) + upAt(b - L); };
    var toView = function (lat, lng) {
      var x = lng * 111320 * PJ.kx, y = -lat * 110540;
      return [r1((x - PJ.minX + PJ.pad) * PJ.scale), r1((y - PJ.minY + PJ.pad) * PJ.scale)];
    };
    var distM = function (a, b) { return Math.hypot(a[0] - b[0], a[1] - b[1]) / PJ.scale; };
    return { D: D, L: L, wrap: wrap, at: at, upAt: upAt, gain: gain, toView: toView, distM: distM, fk: D.meta.scale.km };
  }

  /* Koordinaten auf die Linie: Fusspunkt je Segment, Durchfahrten als Laeufe, je Lauf die naechste Stelle */
  function projectOnRoute(R, xy) {
    var pts = R.D.pts, hits = [];
    for (var i = 0; i < pts.length - 1; i++) {
      var a = pts[i], b = pts[i + 1], dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy;
      var t = l2 ? Math.max(0, Math.min(1, ((xy[0] - a[0]) * dx + (xy[1] - a[1]) * dy) / l2)) : 0;
      var fx = a[0] + dx * t, fy = a[1] + dy * t, d = R.distM(xy, [fx, fy]);
      if (d <= OVERNIGHT_M) hits.push({ i: i, km: a[2] + (b[2] - a[2]) * t, d: d, x: r1(fx), y: r1(fy) });
    }
    if (!hits.length) return [];
    var passes = [], cur = null;
    hits.forEach(function (h) {
      if (cur && h.km - cur.last <= PASS_RUN_KM) { cur.last = h.km; if (h.d < cur.d) { cur.d = h.d; cur.km = h.km; cur.x = h.x; cur.y = h.y; } }
      else { cur = { km: h.km, d: h.d, x: h.x, y: h.y, last: h.km }; passes.push(cur); }
    });
    // Anfang und Ende der Runde sind eine Durchfahrt
    if (passes.length > 1 && passes[0].km < PASS_RUN_KM && R.L - passes[passes.length - 1].last < PASS_RUN_KM) {
      var e = passes.pop(); if (e.d < passes[0].d) { passes[0].d = e.d; passes[0].x = e.x; passes[0].y = e.y; } passes[0].km = 0;
    }
    return passes.map(function (p) { return { km: r1(p.km), d: Math.round(p.d), x: p.x, y: p.y }; }).sort(function (a, b) { return a.d - b.d; });
  }

  /* Hotels aus dem JSON der Section an diese Challenge legen.
     Vorrang: route_access (Geo-Lauf) > planner_geo (Admin) > nichts (nicht planbar, Grund fuer den Editor). */
  function placeHotels(R, raw, key) {
    var D = R.D, list = [], skipped = [];
    var cityOf = function (h) { return (h.city || '').split('·')[0].trim(); };
    raw.forEach(function (h) {
      if (h.active === false) { skipped.push({ hotel: h, why: 'inactive' }); return; }
      if (h.challenges && h.challenges.length && h.challenges.indexOf(D.meta.product) < 0) { skipped.push({ hotel: h, why: 'challenge' }); return; }
      var ra = (h.route_access || []).filter(function (a) { return a.challenge === key && a.entry_km != null; })[0];
      var geo = null, m = /^\s*(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)\s*$/.exec(h.geo || '');
      if (m) geo = R.toView(+m[1], +m[2]);
      var km = null, entryM = null, ex = null, ey = null, place = null;
      if (ra) { km = r1(ra.entry_km * R.fk); entryM = ra.entry_m; place = ra.entry_place || null; var p = R.at(km); ex = p[0]; ey = p[1]; }
      else if (geo) { var pass = projectOnRoute(R, geo)[0]; if (pass) { km = pass.km; entryM = pass.d; ex = pass.x; ey = pass.y; } }
      if (km == null) { skipped.push({ hotel: h, why: geo ? 'far' : 'nogeo' }); return; }
      var town = h.town || place || cityOf(h) || null;
      if (!town) { skipped.push({ hotel: h, why: 'notown' }); return; }
      list.push({
        handle: h.handle, name: h.name, url: h.url, img: h.img || null, town: town, km: km, entry_m: entryM, x: geo ? geo[0] : ex, y: geo ? geo[1] : ey, ex: ex, ey: ey, geo: !!geo,
        rec: h.rec || null, chips: h.chips || [], note: h.note || null,
        silver: (h.silver_plan || []).filter(function (s) { return s.challenge === key; })
      });
    });
    list.sort(function (a, b) { return a.km - b.km; });
    return { hotels: list, skipped: skipped };
  }
  // Entfernung Hotel -> Kartenpunkt in Metern; ohne Koordinaten zaehlt der Einstieg plus Luftlinie zur Strecke
  function hotelDist(R, h, xy) {
    if (h.geo) return R.distM([h.x, h.y], xy);
    return Math.hypot(R.distM([h.ex, h.ey], xy), h.entry_m || 0);
  }

  /* Etappen ab einem Ort fuer N Tage: der Algorithmus aus build-data.mjs, in Shop-Kilometern.
     fixed: feste Etappenorte (N-1 Namen, Vorgabe Fabian 23.09.2026), sonst wird gerechnet. */
  function stages(R, hotels, sp, N, fixed) {
    var D = R.D, L = R.L, s = sp.km;
    var hotelTowns = {}; hotels.forEach(function (h) { hotelTowns[h.town] = true; });
    var towns = D.places.filter(function (p) { return p.town !== false && (p.stage || hotelTowns[p.name]); });
    var candPlaces = towns.map(function (p) { return { kind: 'place', name: p.name, km: p.km }; });
    var candHotels = hotels.map(function (h) { return { kind: 'hotel', name: h.town, km: h.km, hotel: h }; });
    var off = function (c) { return ((c.km - s) % L + L) % L; };
    var near = function (xy) { return hotels.map(function (h) { return { h: h, d: hotelDist(R, h, xy) }; }).filter(function (x) { return x.d <= OVERNIGHT_M; }).sort(function (a, b) { return a.d - b.d || (a.h.rec ? -1 : 1) || (a.h.handle < b.h.handle ? -1 : 1); }); };
    var ref = function (h, d) { return { handle: h.handle, name: h.name, town: h.town, url: h.url, entry_m: h.entry_m, dist_m: Math.round(d) }; };
    var nearestPlace = function (xy, maxM) {
      var best = null;
      D.places.forEach(function (p) { if (p.town === false) return; var d = R.distM([p.x, p.y], xy); if (!best || d < best.d) best = { p: p, d: d }; });
      return best && best.d <= maxM ? best.p.name : null;
    };
    // Feste Orte in Fahrtrichtung suchen; ein Hotel im Ort rastet ein, sonst zaehlt der Ort (Karte "Partnerhotel folgt")
    var fixedEnds = function () {
      if (!fixed || fixed.length !== N - 1) return null;
      var out = [], prev = 0;
      for (var j = 0; j < fixed.length; j++) {
        var want = slug(fixed[j]);
        var p = D.places.filter(function (c) { var o = off(c); return c.town !== false && slug(c.name) === want && o > prev + 1e-6 && o < L - 1e-6; })
          .sort(function (a, b) { return off(a) - off(b); })[0];
        if (!p) return null;
        var hh = hotels.filter(function (x) { return slug(x.town) === want && Math.abs(off(x) - off(p)) <= HOTEL_SNAP_KM; })
          .sort(function (a, b) { return (a.rec ? 0 : 1) - (b.rec ? 0 : 1) || (a.handle < b.handle ? -1 : 1); })[0];
        out.push(hh ? { off: off(hh), name: hh.town, snap: 'hotel', hotel: hh } : { off: off(p), name: p.name, snap: 'place' });
        prev = out[j].off;
      }
      return out;
    };
    var ends = fixedEnds() || [], prevOff = 0, prevName = sp.name;
    for (var i = ends.length ? N : 1; i < N; i++) {
      var ideal = L * i / N;
      var ok = function (c) { var o = off(c); return o > prevOff + 1e-6 && o < L - 1e-6 && Math.abs(o - ideal) <= WINDOW * L; };
      var h = candHotels.filter(ok).map(function (c) { return { c: c, dd: Math.abs(off(c) - ideal) }; }).filter(function (x) { return x.dd <= HOTEL_SNAP_KM; })
        .sort(function (a, b) { return a.dd - b.dd || (a.c.hotel.rec ? -1 : 1) || (a.c.hotel.handle < b.c.hotel.handle ? -1 : 1); })[0];
      var end;
      if (h) end = { off: off(h.c), name: h.c.name, snap: 'hotel', hotel: h.c.hotel };
      else {
        var p = candPlaces.filter(function (c) { return ok(c) && c.name !== sp.name && c.name !== prevName; }).map(function (c) { return { c: c, dd: Math.abs(off(c) - ideal) }; }).sort(function (a, b) { return a.dd - b.dd; })[0];
        if (p) end = { off: off(p.c), name: p.c.name, snap: 'place' };
        else { var xy = R.at(s + ideal); end = { off: ideal, name: nearestPlace(xy, 9000) || ('km ' + Math.round(R.wrap(s + ideal))), snap: 'ideal' }; }
      }
      ends.push(end); prevOff = end.off; prevName = end.name;
    }
    var marks = [{ off: 0, name: sp.name }].concat(ends, [{ off: L, name: sp.name, snap: 'finish' }]);
    var days = [];
    for (var d = 0; d < N; d++) {
      var A = marks[d], B = marks[d + 1], len = B.off - A.off, a = R.wrap(s + A.off), endAt = R.at(s + B.off);
      var overnight = null;
      if (d < N - 1) {
        if (B.snap === 'hotel') overnight = ref(B.hotel, hotelDist(R, B.hotel, endAt));
        else { var n = near(endAt)[0]; if (n) overnight = ref(n.h, n.d); }
      }
      days.push({ day: d + 1, from: A.name, to: B.name, from_km: r1(a), to_km: r1(R.wrap(s + B.off)), km: len, hm: R.gain(a, len), snap: B.snap || null, overnight: overnight });
    }
    roundTo(days, 'km', 5);
    roundTo(days, 'hm', 10);
    return { start: { id: sp.id, name: sp.name, km: sp.km }, days_count: N,
      km_total: days.reduce(function (t, x) { return t + x.km; }, 0), hm_total: days.reduce(function (t, x) { return t + x.hm; }, 0),
      start_hotels: near(R.at(s)).map(function (x) { return ref(x.h, x.d); }), days: days };
  }

  /* Startorte: Orte an der Runde mit aktivem Partnerhotel, Reihenfolge aus der Section, sonst nach km */
  function startTowns(R, hotels, order) {
    var byTown = {}; hotels.forEach(function (h) { (byTown[h.town] = byTown[h.town] || []).push(h); });
    var seen = {}, list = R.D.places.filter(function (p) { return p.town !== false && byTown[p.name] && !seen[p.name] && (seen[p.name] = true); });
    var rank = function (n) { var i = order.indexOf(n); return i < 0 ? 99 : i; };
    return list.sort(function (a, b) { return rank(a.name) - rank(b.name) || a.km - b.km; });
  }

  window.MSPlanner = { prepRoute: prepRoute, placeHotels: placeHotels, stages: stages, startTowns: startTowns, projectOnRoute: projectOnRoute, hotelDist: hotelDist, TIERS: TIERS, slug: slug };
  if (typeof document === 'undefined' || !window.MSChallenge) return;

  /* ================================================================
     Modul "planung"
     ================================================================ */
  var MC = window.MSChallenge, fmt = MC.fmt, reduce = MC.reduce, fitPts = MC.fitPts, pathD = MC.pathD;
  function q(sel, el) { return (el || document).querySelector(sel); }
  function qa(sel, el) { return Array.prototype.slice.call((el || document).querySelectorAll(sel)); }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function tpl(s, map) { s = String(s || ''); Object.keys(map).forEach(function (k) { s = s.split('{{ ' + k + ' }}').join(map[k]).split('__' + k.toUpperCase() + '__').join(map[k]); }); return s; }
  function jsonOf(sel, el) { try { var t = q(sel, el); return t ? JSON.parse(t.textContent) : null; } catch (e) { return null; } }
  var routeCache = {};
  function loadRoute(url) {
    if (!routeCache[url]) {
      routeCache[url] = fetch(url, { cache: 'force-cache' }).then(function (r) { if (!r.ok) throw new Error('Streckendaten ' + r.status); return r.json(); });
      routeCache[url].catch(function () { delete routeCache[url]; });
    }
    return routeCache[url];
  }
  var ICON = {
    chev: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9.5 6 6 6-6"/></svg>',
    check: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>',
    flag: '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 21V4h12l-2.5 4L17 12H5"/></svg>',
    bed: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 18v-8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v8M3 14h18M6 8V6h5v2M13 8V6h5v2"/></svg>',
    info: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></svg>',
    arrow: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>'
  };

  MC.register('planung', function (ctx) {
    var panel = ctx.panel, root = panel.closest('.ms-ct') || panel;
    var T = jsonOf('[data-ms-planung-i18n]', panel) || {};
    var CFG = jsonOf('[data-ms-planung-cfg]', panel) || {};
    var RAW = jsonOf('[data-ms-planner-hotels]', panel) || [];
    var pickBox = q('[data-ms-pick]', panel), body = q('[data-ms-plan-body]', panel), diag = q('[data-ms-plan-diag]', panel);
    var CH = {};       // je Challenge: { R, hotels, skipped, towns }
    var P = { ch: ctx.key, start: null, tier: 'bronze', variant: 'etappe' };
    var disposers = [], entered = false, rendered = false, toastT = null;
    var stageMemo = {};

    /* Andere Bausteine des Reiters (Wann fahren?) folgen der Stufe ueber dieses Ereignis */
    function tierChanged() { try { document.dispatchEvent(new CustomEvent('ms:planner-tier', { detail: { tier: P.tier } })); } catch (e) {} }

    /* ---------- Zustand aus URL und Kaufbox ---------- */
    var tierOfValue = function (v) { v = slug(v); return TIER_KEYS.indexOf(v) >= 0 ? v : (v === 'silver' ? 'silber' : null); };
    var tierRadios = function () { return qa('input[data-ms-tier]'); };
    var buyboxTier = function () {
      var on = q('input[data-ms-tier]:checked');
      return on ? tierOfValue(on.value) : null;
    };
    (function initState() {
      var u = new URLSearchParams(location.search);
      var t = tierOfValue(u.get('ms_tier') || '') || buyboxTier();
      if (t) P.tier = t;
      if (u.get('ms_start')) P.start = u.get('ms_start');
      if (u.get('ms_variant') === 'basecamp') P.variant = 'basecamp';
      if (P.tier !== 'silber') P.variant = 'etappe';
    })();

    /* Zustand in die URL schreiben (Vorgabe Fabian 17.09.2026): Zurueck vom Hotel stellt den Plan
       wieder her, ohne neu zu waehlen. replaceState, kein neuer Verlaufseintrag; nicht im Editor. */
    function persist() {
      if (window.Shopify && window.Shopify.designMode) return;
      try {
        var u = new URL(location.href), sp = u.searchParams;
        sp.set('ms_tier', P.tier);
        if (P.start) sp.set('ms_start', P.start); else sp.delete('ms_start');
        if (P.tier === 'silber') sp.set('ms_variant', P.variant); else sp.delete('ms_variant');
        if (P.ch && P.ch !== ctx.key) sp.set('ms_ch', P.ch); else sp.delete('ms_ch');
        history.replaceState(history.state, '', u.pathname + '?' + sp.toString() + u.hash);
      } catch (e) {}
    }

    /* Rueckweg vom Hotel. Der Link soll wieder im Tab Planung landen, deshalb "ms_goto":
       ein Hash wuerde beim Aufruf nichts bewegen (Vorgabe: die Seite springt beim Laden nie). */
    function planBackUrl() {
      try {
        var u = new URL(location.href);
        u.searchParams.set('ms_goto', 'planung');
        return u.pathname + '?' + u.searchParams.toString();
      } catch (e) { return location.pathname + location.search; }
    }

    /* ---------- Daten je Challenge ---------- */
    function ensure(k) {
      if (CH[k]) return Promise.resolve(CH[k]);
      var url = CFG.routes && CFG.routes[k];
      var p = k === ctx.key ? ctx.data() : loadRoute(url);
      return p.then(function (D) {
        var R = prepRoute(D), placed = placeHotels(R, RAW, k);
        CH[k] = { R: R, D: D, hotels: placed.hotels, skipped: placed.skipped, towns: startTowns(R, placed.hotels, CFG.start_order || []) };
        return CH[k];
      });
    }
    /* Startorte fuer die gewaehlte Stufe (Vorgabe Fabian 22.09.2026: Bronze nur Wernigerode,
       Silber und Gold auch Braunlage). Trifft die Liste keinen Ort, bleiben alle Orte waehlbar. */
    function townsFor(k) {
      var all = CH[k].towns, want = (CFG.start_towns || {})[P.tier];
      if (!want || !want.length) return all;
      var ok = {}; want.forEach(function (n) { ok[slug(n)] = true; });
      var list = all.filter(function (p) { return ok[slug(p.name)]; });
      return list.length ? list : all;
    }
    function townOf(k, name) {
      var towns = townsFor(k); if (!towns.length) return null;
      return towns.filter(function (p) { return p.name === name || p.id === name || slug(p.name) === slug(name || ''); })[0] || towns[0];
    }
    /* Feste Etappenorte aus der Section: Schluessel "Challenge Stufe Startort", verglichen ueber slug */
    var STAGE_TOWNS = {};
    Object.keys(CFG.stage_towns || {}).forEach(function (key) { STAGE_TOWNS[slug(key).replace(/(^|-)silver(-|$)/, '$1silber$2')] = CFG.stage_towns[key]; });
    function stagesFor(k, sp, N) {
      var id = k + '|' + sp.id + '|' + N, tier = TIER_KEYS.filter(function (t) { return TIERS[t] === N; })[0];
      if (!stageMemo[id]) stageMemo[id] = stages(CH[k].R, CH[k].hotels, sp, N, STAGE_TOWNS[slug(k + ' ' + tier + ' ' + sp.name)]);
      return stageMemo[id];
    }
    var hotelsIn = function (k, town) { return CH[k].hotels.filter(function (h) { return h.town === town; }); };
    var tierDays = function (t) { return TIERS[t] === 1 ? T.day_one : tpl(T.days_n, { n: TIERS[t] }); };
    var tierName = function (t) { return (T.tiers && T.tiers[t]) || t; };
    var colorOf = function (k) { return k === ctx.key ? CH[k].D.meta.color : (CH[ctx.key].D.meta.others[k] || {}).color; };

    /* ---------- Auswahlmenue (W3C Select-Only Combobox, Varianten Panel und Blatt) ---------- */
    var DD = { open: null, sets: {}, heads: {}, seq: 0, layer: null, type: '', typeT: null };
    function ddField(id, label, value, opts, icon, head) {
      DD.sets[id] = opts; DD.heads[id] = head || null;
      return '<div class="ct-psel" data-ddfield="' + id + '">' + (icon ? '<span class="ct-psel__ico" aria-hidden="true">' + icon + '</span>' : '') +
        '<span class="ct-psel__t" id="ct-ddl-' + id + '">' + esc(label) + '</span>' +
        '<button type="button" class="ct-psel__v" data-ddbtn="' + id + '" role="combobox" aria-haspopup="listbox" aria-expanded="false" aria-labelledby="ct-ddl-' + id + ' ct-ddv-' + id + '"><b id="ct-ddv-' + id + '">' + value + '</b>' + ICON.chev + '</button></div>';
    }
    function ddLayer() {
      if (DD.layer) return DD.layer;
      var l = document.createElement('div');
      l.className = 'ct-ddl ms-ct-tokens'; l.hidden = true;
      l.innerHTML = '<div class="ct-ddl__bg" data-ddbg></div><div class="ct-dd" role="listbox" tabindex="-1" data-ddlist></div>';
      document.body.appendChild(l);
      l.addEventListener('click', function (e) {
        if (e.target.closest('[data-ddbg]')) { ddClose(); return; }
        var o = e.target.closest('.ct-dd__o'); if (o && o.getAttribute('aria-disabled') !== 'true') ddCommit(+o.getAttribute('data-i'));
      });
      l.addEventListener('mousemove', function (e) { var o = e.target.closest('.ct-dd__o'); if (o) ddActive(+o.getAttribute('data-i'), true); });
      DD.layer = l;
      disposers.push(function () { if (l.parentNode) l.parentNode.removeChild(l); DD.layer = null; });
      return l;
    }
    function ddOpen(id, btn) {
      var opts = DD.sets[id]; if (!opts || !opts.length) return;
      var l = ddLayer(), list = q('[data-ddlist]', l);
      var cs = getComputedStyle(root);
      ['--ct-c', '--ct-c-dark', '--ct-c-deep', '--ct-c-tint', '--ct-c-soft'].forEach(function (n) { l.style.setProperty(n, cs.getPropertyValue(n).trim()); });
      var sheet = panel.clientWidth <= 600;
      l.classList.toggle('is-sheet', sheet);
      list.id = 'ct-ddlist-' + (++DD.seq);
      list.setAttribute('aria-labelledby', 'ct-ddl-' + id);
      var head = DD.heads[id];
      list.innerHTML = (sheet ? '<span class="ct-dd__head"><b>' + esc(head ? head.t : btn.parentElement.querySelector('.ct-psel__t').textContent) + '</b>' + (head && head.s ? '<small>' + esc(head.s) + '</small>' : '') + '</span>' : '') +
        opts.map(function (o, i) {
          return '<div class="ct-dd__o' + (o.on ? ' is-on' : '') + '" role="option" data-i="' + i + '" id="ct-ddo-' + DD.seq + '-' + i + '" aria-selected="' + (o.on ? 'true' : 'false') + '"' + (o.disabled ? ' aria-disabled="true"' : '') + '>' +
            (o.pic ? '<span class="ct-dd__img">' + o.pic + '</span>' : '') + (o.dot ? '<i class="ct-dot" style="--dc:' + esc(o.dot) + '"></i>' : '') +
            '<span class="ct-dd__txt"><b class="ct-dd__t">' + esc(o.label) + '</b>' + (o.sub ? '<small class="ct-dd__s">' + esc(o.sub) + '</small>' : '') + '</span>' +
            (o.tag ? '<span class="ct-dd__tag">' + esc(o.tag) + '</span>' : '') + (o.meta ? '<small class="ct-dd__m">' + esc(o.meta) + '</small>' : '') +
            '<span class="ct-dd__c" aria-hidden="true">' + ICON.check + '</span></div>';
        }).join('');
      DD.open = { id: id, btn: btn, opts: opts, active: Math.max(0, opts.map(function (o) { return !!o.on; }).indexOf(true)) };
      btn.setAttribute('aria-expanded', 'true'); btn.setAttribute('aria-controls', list.id);
      l.hidden = false; list.style.cssText = '';
      var box = btn.closest('.ct-psel') || btn, rb = box.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight;
      if (sheet) {
        document.body.classList.add('overflow-hidden');
        if (!reduce() && list.animate) {
          q('[data-ddbg]', l).animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
          list.animate([{ transform: 'translateY(100%)' }, { transform: 'none' }], { duration: 500, easing: 'cubic-bezier(.32,.72,0,1)' });
        }
      } else {
        var maxH = Math.min(340, vh - 24);
        list.style.maxHeight = maxH + 'px';
        list.style.minWidth = Math.max(rb.width, 240).toFixed(0) + 'px';
        list.style.maxWidth = Math.max(260, vw - 32).toFixed(0) + 'px';
        var ph = Math.min(list.scrollHeight, maxH), pw = list.getBoundingClientRect().width, top = rb.bottom + 8, origin = 'left top';
        if (top + ph > vh - 8) { top = rb.top - ph - 8; origin = 'left bottom'; }
        list.style.top = Math.max(8, Math.min(top, vh - ph - 8)).toFixed(0) + 'px';
        list.style.left = Math.max(8, Math.min(rb.left, vw - pw - 8)).toFixed(0) + 'px';
        list.style.transformOrigin = origin;
        if (!reduce() && list.animate) list.animate([{ opacity: 0, transform: 'scale(.95) translateY(-6px)' }, { opacity: 1, transform: 'none' }], { duration: 200, easing: 'cubic-bezier(.23,1,.32,1)' });
      }
      ddActive(DD.open.active);
    }
    function ddActive(i, quiet) {
      if (!DD.open) return;
      var items = qa('.ct-dd__o', DD.layer); if (!items.length) return;
      i = Math.max(0, Math.min(i, items.length - 1)); DD.open.active = i;
      items.forEach(function (n, k) { n.classList.toggle('is-active', k === i); });
      DD.open.btn.setAttribute('aria-activedescendant', items[i].id);
      if (!quiet) items[i].scrollIntoView({ block: 'nearest' });
    }
    function ddCommit(i) {
      if (!DD.open) return;
      var id = DD.open.id, o = DD.open.opts[i];
      ddClose();
      if (o && !o.disabled) onSelect(id, o.v);
    }
    function ddClose(instant) {
      if (!DD.open || !DD.layer) return;
      var btn = DD.open.btn, l = DD.layer, list = q('[data-ddlist]', l), sheet = l.classList.contains('is-sheet');
      DD.open = null;
      btn.setAttribute('aria-expanded', 'false'); btn.removeAttribute('aria-activedescendant');
      document.body.classList.remove('overflow-hidden');
      var done = function () { l.hidden = true; l.getAnimations({ subtree: true }).forEach(function (a) { a.cancel(); }); };
      if (btn.isConnected) btn.focus({ preventScroll: true });
      if (instant === true || reduce() || !list.animate) { done(); return; }
      q('[data-ddbg]', l).animate([{ opacity: 1 }, { opacity: 0 }], { duration: sheet ? 260 : 140, easing: 'ease-out', fill: 'forwards' });
      var a = sheet ? list.animate([{ transform: 'none' }, { transform: 'translateY(100%)' }], { duration: 320, easing: 'cubic-bezier(.32,.72,0,1)', fill: 'forwards' })
        : list.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(.98)' }], { duration: 140, easing: 'ease-out', fill: 'forwards' });
      a.onfinish = done;
    }
    function ddTypeahead(ch) {
      if (!DD.open) return;
      clearTimeout(DD.typeT); DD.type += ch.toLowerCase();
      DD.typeT = setTimeout(function () { DD.type = ''; }, 600);
      var opts = DD.open.opts, single = DD.type.length > 1 && DD.type.split('').every(function (c) { return c === DD.type[0]; });
      var qq = single ? DD.type[0] : DD.type, from = single ? DD.open.active + 1 : 0;
      for (var i = 0; i < opts.length; i++) { var k = (from + i) % opts.length; if (opts[k].label.toLowerCase().indexOf(qq) === 0) { ddActive(k); return; } }
    }
    var onKey = function (e) {
      var btn = e.target.closest && e.target.closest('[data-ddbtn]');
      if (!DD.open) {
        if (!btn || !panel.contains(btn)) return;
        var k = e.key;
        if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ' || k === 'Home' || k === 'End') {
          e.preventDefault(); ddOpen(btn.getAttribute('data-ddbtn'), btn);
          if (k === 'Home') ddActive(0); if (k === 'End') ddActive(DD.open.opts.length - 1);
          if (k === 'ArrowDown') ddActive(DD.open.active + 1); if (k === 'ArrowUp') ddActive(DD.open.active - 1);
          return;
        }
        if (k.length === 1 && /\S/.test(k)) { ddOpen(btn.getAttribute('data-ddbtn'), btn); ddTypeahead(k); }
        return;
      }
      var n = DD.open.opts.length;
      switch (e.key) {
        case 'Escape': e.preventDefault(); e.stopPropagation(); ddClose(); break;
        case 'ArrowDown': e.preventDefault(); ddActive(DD.open.active + 1); break;
        case 'ArrowUp': e.preventDefault(); if (e.altKey) ddCommit(DD.open.active); else ddActive(DD.open.active - 1); break;
        case 'Home': e.preventDefault(); ddActive(0); break;
        case 'End': e.preventDefault(); ddActive(n - 1); break;
        case 'PageDown': e.preventDefault(); ddActive(DD.open.active + 10); break;
        case 'PageUp': e.preventDefault(); ddActive(DD.open.active - 10); break;
        case 'Enter': case ' ': e.preventDefault(); ddCommit(DD.open.active); break;
        case 'Tab': ddCommit(DD.open.active); break;
        default: if (e.key.length === 1 && /\S/.test(e.key)) ddTypeahead(e.key);
      }
    };
    var onDocClick = function (e) {
      var btn = e.target.closest('[data-ddbtn]');
      if (btn && panel.contains(btn)) { e.preventDefault(); if (DD.open && DD.open.btn === btn) { ddClose(); return; } if (DD.open) ddClose(true); ddOpen(btn.getAttribute('data-ddbtn'), btn); return; }
      if (DD.open && !e.target.closest('.ct-ddl')) ddClose();
    };
    var onWinScroll = function () { if (DD.open && !DD.layer.classList.contains('is-sheet')) ddClose(true); };
    var onWinResize = function () { if (DD.open) ddClose(true); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('click', onDocClick);
    window.addEventListener('scroll', onWinScroll, { passive: true });
    window.addEventListener('resize', onWinResize);
    disposers.push(function () { document.removeEventListener('keydown', onKey); document.removeEventListener('click', onDocClick); window.removeEventListener('scroll', onWinScroll); window.removeEventListener('resize', onWinResize); });

    /* ---------- Auswahl rendern ---------- */
    function townPic(k, p) {
      var c = CH[k], R = 78;
      return '<svg viewBox="' + (p.x - R).toFixed(1) + ' ' + (p.y - R).toFixed(1) + ' ' + R * 2 + ' ' + R * 2 + '" aria-hidden="true" focusable="false"><path class="ct-ddmap__r" d="' + pathD(c.D.pts) + '"/><circle class="ct-ddmap__d" cx="' + p.x + '" cy="' + p.y + '" r="13"/></svg>';
    }
    function renderPick(animiert) {
      var k = P.ch, c = CH[k], sp = townOf(k, P.start);
      if (sp) P.start = sp.name;
      var altFach = q('[data-ms-varslot]', pickBox), varVorher = !!(altFach && altFach.firstElementChild);
      var hAlt = varVorher ? altFach.offsetHeight : 0, varAlt = varVorher ? altFach.innerHTML : '';
      var keys = [ctx.key].concat(Object.keys(CH[ctx.key].D.meta.others || {}));
      var chOpts = keys.map(function (x) {
        var o = x === ctx.key ? CH[ctx.key].D.meta : CH[ctx.key].D.meta.others[x];
        return { v: x, label: (T.series || 'Harter Brocken') + ' ' + o.label, meta: fmt(o.shop.km) + ' ' + (T.km || 'km'), dot: o.color, on: x === k };
      });
      var startOpts = townsFor(k).map(function (p) {
        var hs = hotelsIn(k, p.name);
        return { v: p.name, label: p.name + (bcShown(p.name) ? ' (' + T.variant_basecamp + ')' : ''), on: p.name === P.start, pic: townPic(k, p), sub: hs.length === 1 ? hs[0].name : tpl(T.hotels_n, { n: hs.length }), tag: (CFG.rec_tags || {})[p.name] || '' };
      });
      var tierOpts = TIER_KEYS.map(function (t) { return { v: t, label: tierName(t), meta: tierDays(t), dot: CFG.tier_colors ? CFG.tier_colors[t] : '', on: t === P.tier }; });
      var chLabel = k === ctx.key ? c.D.meta.label : CH[ctx.key].D.meta.others[k].label;
      pickBox.innerHTML = '<div class="ct-pbar">' +
        ddField('ch', T.pick_challenge, '<span class="ct-psel__pre">' + esc(T.series || 'Harter Brocken') + '</span><span class="ct-psel__cat">' + esc(chLabel) + '</span>', chOpts, CFG.icons ? CFG.icons.ch : '') +
        ddField('start', T.pick_start, sp ? esc(sp.name) + (bcShown(sp.name) ? ' <small>(' + esc(T.variant_basecamp) + ')</small>' : '') : esc(T.no_towns), startOpts, CFG.icons ? CFG.icons.start : '', { t: T.start_head, s: T.start_sub }) +
        ddField('tier', T.pick_tier, esc(tierName(P.tier)) + ' <small>' + esc(tierDays(P.tier)) + '</small>', tierOpts, CFG.icons ? CFG.icons.tier : '') + '</div>' +
        '<div class="ct-pvarslot" data-ms-varslot></div>';
      syncVar(animiert, varVorher, hAlt, varAlt);
    }
    var basecampPlan = function (k) {
      var c = CH[k], plans = [];
      c.hotels.forEach(function (h) { h.silver.forEach(function (s) { plans.push({ hotel: h, s: s }); }); });
      if (!plans.length) return null;
      var mine = plans.filter(function (p) { return p.hotel.town === P.start; });
      var pool = mine.length ? mine : plans;
      // Kuratierte Plaene (Split-Point von Mountain Squad aus GPX) vor berechneten, darin die empfohlenen zuerst
      var rank = function (p) { return (p.s.curated ? 0 : 2) + (p.s.recommended ? 0 : 1); };
      return pool.slice().sort(function (a, b) { return rank(a) - rank(b) || a.hotel.km - b.hotel.km; })[0];
    };
    function varHTML() {
      var bc = basecampPlan(P.ch), on = function (v) { return P.variant === v; };
      return '<div class="ct-pvarwrap"><div class="ct-pvar"><span class="ct-pvar__t">' + esc(T.variant_q) + '</span><div class="ct-pvar__opts">' +
        '<button type="button" class="ct-pvar__b' + (on('etappe') ? ' is-on' : '') + '" data-pick-variant="etappe" aria-pressed="' + on('etappe') + '"><b>' + esc(T.variant_etappe) + '</b><span>' + esc(T.variant_etappe_text) + '</span></button>' +
        '<button type="button" class="ct-pvar__b' + (on('basecamp') ? ' is-on' : '') + '" data-pick-variant="basecamp" aria-pressed="' + on('basecamp') + '"' + (bc ? '' : ' disabled') + '><b>' + esc(T.variant_basecamp) + '</b><span>' + esc(bc ? T.variant_basecamp_text : T.basecamp_missing) + '</span></button>' +
        '</div></div></div>';
    }
    function syncVar(animiert, warDa, hAlt, htmlAlt) {
      var fach = q('[data-ms-varslot]', pickBox), soll = P.tier === 'silber';
      if (soll) fach.innerHTML = varHTML();
      if (!animiert || reduce() || !fach.animate || soll === warDa) return;
      fach.style.overflow = 'hidden';
      if (soll) {
        var hNeu = fach.scrollHeight;
        fach.animate([{ height: '0px', opacity: 0 }, { height: hNeu + 'px', opacity: 1 }], { duration: 440, easing: 'cubic-bezier(.22,1,.36,1)' }).onfinish = function () { fach.style.overflow = ''; };
      } else {
        fach.innerHTML = htmlAlt; fach.style.pointerEvents = 'none';
        fach.animate([{ height: hAlt + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(.32,0,.28,1)' }).onfinish = function () { fach.innerHTML = ''; fach.style.overflow = ''; fach.style.pointerEvents = ''; };
      }
    }

    /* Nach einem Stufenwechsel muss der Startort zur Stufe passen */
    function syncStart() { var sp = CH[P.ch] && townOf(P.ch, P.start); P.start = sp ? sp.name : null; }
    /* Silber ab Braunlage ist Basecamp (Vorgabe Fabian 23.09.2026): zwei Etappen ab dort ergeben keinen Sinn.
       Greift bei jedem Wechsel von Startort oder Stufe; die Schalter bleiben danach frei waehlbar. */
    var isBcTown = function (name) { return (CFG.basecamp_towns || []).some(function (n) { return slug(n) === slug(name || ''); }); };
    function bcShown(name) { return P.tier === 'silber' && isBcTown(name) && !!basecampPlan(P.ch); }
    function autoVariant() { P.variant = P.tier === 'silber' && CH[P.ch] && bcShown(P.start) ? 'basecamp' : 'etappe'; }
    function onSelect(id, v) {
      if (id === 'ch') { if (v !== P.ch) switchChallenge(v); return; }
      if (id === 'start') { P.start = v; autoVariant(); renderPick(); swapPlan(); persist(); return; }
      if (id === 'tier') { if (v === P.tier) return; P.tier = v; syncStart(); autoVariant(); renderPick(true); swapPlan(); persist(); tierChanged(); }
    }
    /* Challenge wechseln, ohne die Seite zu verlassen: der Kopf bleibt stehen, die Farbe der
       Section wechselt mit (die abgeleiteten Toene rechnen sich aus --ct-c), unten tauscht der Plan */
    function switchChallenge(k) {
      ensure(k).then(function () {
        P.ch = k;
        var col = colorOf(k);
        if (k === ctx.key) root.style.removeProperty('--ct-c'); else if (col) root.style.setProperty('--ct-c', col);
        var sp = townOf(k, P.start); P.start = sp ? sp.name : null;
        if (P.variant === 'basecamp' && !basecampPlan(k)) P.variant = 'etappe';
        renderPick(); swapPlan(); persist();
      }).catch(function (e) { if (window.console) console.error('ms-challenge-planner', e); });
    }

    /* ---------- Plan ---------- */
    function swapPlan() {
      if (!rendered || reduce() || !body.animate) { renderPlan(); return; }
      var hVor = body.offsetHeight;
      body.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, easing: 'ease-out', fill: 'forwards' }).onfinish = function () {
        renderPlan(true);
        body.getAnimations().forEach(function (a) { a.cancel(); });
        var hNach = body.offsetHeight, dauer = Math.round(Math.min(760, 380 + Math.abs(hNach - hVor) * 0.42));
        body.style.overflow = 'hidden';
        body.animate([{ opacity: 0, transform: 'translateY(10px)', height: hVor + 'px' }, { opacity: 1, transform: 'none', height: hNach + 'px' }], { duration: dauer, easing: 'cubic-bezier(.4,0,.2,1)' }).onfinish = function () { body.style.overflow = ''; };
      };
    }
    var revealIO = null;
    function armReveals(scope, weich) {
      var els = qa('[data-ms-rv]', scope), vh = window.innerHeight, n = 0;
      var noSize = scope.getBoundingClientRect().height === 0;
      els.forEach(function (el, i) {
        var r = el.getBoundingClientRect(), inView = noSize ? i < 4 : (r.top < vh && r.bottom > 0);
        if (weich && inView) { el.removeAttribute('data-ms-rv'); el.classList.remove('is-pre'); return; }
        el.classList.add('is-pre');
        el.style.setProperty('--d', inView ? (Math.min(n++, 5) * 0.07).toFixed(2) + 's' : '0s');
      });
      if (!revealIO && window.IntersectionObserver) {
        revealIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) { if (en.isIntersecting) { en.target.classList.remove('is-pre'); revealIO.unobserve(en.target); } });
        }, { rootMargin: '0px 0px -8% 0px' });
        disposers.push(function () { revealIO.disconnect(); revealIO = null; });
      }
      qa('[data-ms-rv].is-pre', scope).forEach(function (el) {
        if (reduce() || !revealIO) { el.classList.remove('is-pre'); return; }
        var r = el.getBoundingClientRect();
        if (r.top < vh && r.bottom > 0) requestAnimationFrame(function () { el.classList.remove('is-pre'); }); else revealIO.observe(el);
      });
    }
    function renderPlan(weich) {
      var k = P.ch, sp = townOf(k, P.start);
      if (!sp) { body.innerHTML = '<p class="ct-pl-empty">' + esc(T.no_towns_text) + '</p>'; rendered = true; return; }
      P.start = sp.name;
      body.innerHTML = P.tier === 'silber' && P.variant === 'basecamp' ? basecampHTML(k, sp) : stagesHTML(k, sp);
      rendered = true;
      armReveals(body, weich);
      routeLine(body);
      if (diag) renderDiag();
    }

    /* Karten der Tage: gefahrenes Stueck farbig, Rest blass */
    function segPts(D, a, b) {
      var P2 = D.profile, pts = b > a ? P2.filter(function (p) { return p[0] >= a - 0.01 && p[0] <= b + 0.01; }) : P2.filter(function (p) { return p[0] >= a - 0.01; }).concat(P2.filter(function (p) { return p[0] <= b + 0.01; }));
      return pts.length > 1 ? 'M' + pts.map(function (p) { return p[2] + ' ' + p[3]; }).join('L') : '';
    }
    function pointAt(D, km) { return D.profile.reduce(function (best, p) { return Math.abs(p[0] - km) < Math.abs(best[0] - km) ? p : best; }, D.profile[0]); }
    function mapSVG(D, Z, inner, over, label) {
      var d = pathD(D.pts);
      return '<svg viewBox="0 0 1000 658" preserveAspectRatio="xMidYMid meet" role="img" aria-label="' + esc(label) + '"><g style="--s:' + Z.s.toFixed(4) + '" transform="' + Z.g + '">' +
        '<path class="ct-map__halo" style="stroke-width:calc(8px / var(--s))" d="' + d + '"/><path class="ct-map__route ct-map__route--faint" style="stroke-width:calc(2.4px / var(--s))" d="' + d + '"/>' + (inner || '') + '</g>' + (over || '') + '</svg>';
    }
    var dayPath = function (D, von, bis) { return (von == null || bis == null) ? '' : '<path class="ct-map__day" style="stroke-width:calc(5px / var(--s))" d="' + segPts(D, von, bis) + '"/>'; };
    var pt = function (Z, p, cls, r, fill) { return '<g class="ct-map__pt"><circle class="' + (cls || '') + '" cx="' + Z.X(p[0]) + '" cy="' + Z.Y(p[1]) + '" r="' + r + '" fill="' + fill + '" stroke-width="3"/></g>'; };
    function dayCard(D, Z, n, a, b, km, hm, von, bis, over) {
      return '<div class="ct-daycard"><div class="ct-daycard__txt"><span class="ct-label">' + esc(tpl(T.day_n, { n: n })) + '</span><div class="ct-daycard__route">' + a + ' ' + esc(T.to) + ' ' + b + '</div>' +
        '<div class="ct-daycard__stats"><span>' + esc(T.approx) + ' <b>' + fmt(km) + '</b> ' + esc(T.km) + '</span><span>' + esc(T.approx) + ' <b>' + fmt(hm) + '</b> ' + esc(T.hm) + '</span></div></div>' +
        '<div class="ct-daycard__map">' + mapSVG(D, Z, dayPath(D, von, bis), over, tpl(T.map_label, { n: n })) + '</div></div>';
    }
    /* Hotelkarte quer (Vorgabe Fabian 22.09.2026): volle Breite, Text links, Foto rechts,
       gleiche Teilung und Hoehe wie die Tageskarte, damit Nacht und Tag gleich lang sind */
    function hotelCard(k, h) {
      var chips = (h.chips || []).concat(h.silver.length ? [T.basecamp_chip] : []);
      var facts = (h.entry_m != null ? ['<b>' + esc(tpl(T.to_route, { m: fmt(Math.round(h.entry_m)) })) + '</b>'] : []).concat(h.note ? [esc(h.note)] : []).concat(chips.map(esc));
      return '<article class="ct-stay' + (h.img ? '' : ' ct-stay--noimg') + '">' +
        '<span class="ct-stay__body">' +
        '<span class="ct-stay__top"><span class="ct-stay__kind">' + esc(T.hotel_kind) + '<i>·</i>' + esc(h.town) + '</span></span>' +
        '<h4>' + esc(h.name) + '</h4>' +
        (facts.length ? '<span class="ct-stay__facts">' + facts.map(function (f) { return '<span>' + f + '</span>'; }).join('') + '</span>' : '') +
        (h.url ? '<a class="ct-btn ct-btn--sm ct-stay__go" href="' + esc(h.url) + '">' + esc(T.hotel_go) + '</a>' : '') + '</span>' +
        (h.img ? '<span class="ct-stay__img"><img alt="' + esc(h.name) + '" src="' + esc(h.img) + '" loading="lazy" decoding="async" width="480" height="300"></span>' : '<span class="ct-stay__img ct-stay__img--empty" aria-hidden="true">' + ICON.bed + '</span>') +
        (h.rec ? '<span class="ct-stay__rec">' + esc(h.rec) + '</span>' : '') +
        '</article>';
    }
    function placeholderCard(town) {
      var ph = CFG.placeholder || {};
      return '<article class="ct-stay ct-stay--soon' + (ph.img ? '' : ' ct-stay--noimg') + '">' +
        '<span class="ct-stay__body">' +
        '<span class="ct-stay__top"><span class="ct-stay__kind">' + esc(T.hotel_kind) + '<i>·</i>' + esc(town) + '</span></span>' +
        '<h4>' + esc(ph.title || T.placeholder_title) + '</h4>' +
        '<p class="ct-stay__p">' + esc(tpl(ph.text || T.placeholder_text, { town: town })) + '</p></span>' +
        (ph.img ? '<span class="ct-stay__img"><img alt="" src="' + esc(ph.img) + '" loading="lazy" decoding="async" width="480" height="300"></span>' : '<span class="ct-stay__img ct-stay__img--empty" aria-hidden="true">' + ICON.bed + '</span>') +
        '</article>';
    }
    function stayCards(k, town, fallback) {
      var hs = hotelsIn(k, town);
      if (!hs.length && fallback) hs = CH[k].hotels.filter(function (h) { return h.handle === fallback.handle; });
      return '<div class="ct-pl-stay">' + (hs.length ? hs.map(function (h) { return hotelCard(k, h); }).join('') : placeholderCard(town)) + '</div>';
    }
    var tlWrap = function (inner) {
      return '<div class="ct-tl"><svg class="ct-tl__route" data-tlsvg aria-hidden="true" focusable="false"><defs><clipPath id="ct-tlclip-' + (++DD.seq) + '"><rect data-tlrect x="0" y="0" width="0" height="0"/></clipPath></defs>' +
        '<path class="ct-tl__base" data-tlbase d=""/><path class="ct-tl__on" data-tlon d="" clip-path="url(#ct-tlclip-' + DD.seq + ')"/></svg>' + inner + '</div>';
    };
    var goalItem = function (place, note) { return '<div class="ct-tl__item" data-ms-rv><span class="ct-tl__dot ct-tl__dot--goal">' + ICON.flag + '</span><h3>' + esc(T.goal) + ' <span>· ' + esc(place) + '</span></h3><p class="ct-tl__note">' + esc(note) + '</p></div>'; };
    var sumStrip = function (tier, rest, nums) { return '<div class="ct-pl-sum" data-ms-rv><h3><span class="ct-grad">' + esc(tier) + '</span> ' + esc(rest) + '</h3><div class="ct-pl-sum__nums">' + nums.map(function (x) { return '<span><b>' + x[0] + '</b> ' + esc(x[1]) + '</span>'; }).join('') + '</div></div>'; };
    var timeLine = function (t) { var s = T.tier_time && T.tier_time[t]; return s ? '<p class="ct-tl__time">' + esc(s) + '</p>' : ''; };
    function planCta(k, tier, sub) {
      var own = k === ctx.key, prod = !own && CFG.products && CFG.products[k];
      var btn = own ? '<button type="button" class="ct-btn" data-ms-plan-take="' + tier + '">' + esc(tpl(T.cta_btn, { tier: tierName(tier) })) + ' ' + ICON.arrow + '</button>'
        : (prod ? '<a class="ct-btn" href="' + esc(prod + '?ms_tier=' + tier + '&ms_start=' + encodeURIComponent(P.start || '') + '&ms_variant=' + P.variant + '&ms_goto=planung') + '">' + esc(tpl(T.cta_other, { label: (CH[ctx.key].D.meta.others[k] || {}).label || k })) + ' ' + ICON.arrow + '</a>' : '');
      return '<div class="ct-plan-cta" data-ms-rv><div><b>' + esc(T.cta_title) + '</b><span class="ct-plan-cta__sub">' + esc(tierName(tier)) + ' · ' + esc(sub) + '</span></div>' + btn + '</div>';
    }
    function stagesHTML(k, sp) {
      var c = CH[k], D = c.D, st = stagesFor(k, sp, TIERS[P.tier]), n = st.days.length, Z = fitPts(D.pts, 40), items = '';
      st.days.forEach(function (d, i) {
        var place = i === 0 ? st.start.name : st.days[i - 1].to;
        var fallback = i === 0 ? st.start_hotels[0] : st.days[i - 1].overnight;
        var note = i === 0 ? tpl(T.note_start, { town: place }) : tpl(T.note_after, { n: i, town: place });
        items += '<div class="ct-tl__item" data-ms-rv><span class="ct-tl__dot"></span><h3>' + esc(tpl(T.night_n, { n: i + 1 })) + ' <span>· ' + esc(place) + '</span></h3><p class="ct-tl__note">' + esc(note) + '</p>' + stayCards(k, place, fallback) + '</div>';
        var a = pointAt(D, d.from_km), b = pointAt(D, d.to_km);
        var over = pt(Z, [a[2], a[3]], '', 8, '#111213') + (n > 1 ? pt(Z, [b[2], b[3]], '', 8, 'var(--ct-c)') : '');
        var route = n === 1 ? tpl(T.once_round, { town: esc(d.from) }) : esc(d.from) + ' ' + esc(T.to) + ' ' + esc(d.to);
        items += '<div class="ct-tl__item" data-ms-rv><div class="ct-daycard"><div class="ct-daycard__txt"><span class="ct-label">' + esc(tpl(T.day_n, { n: d.day })) + '</span><div class="ct-daycard__route">' + route + '</div>' +
          '<div class="ct-daycard__stats"><span>' + esc(T.approx) + ' <b>' + fmt(d.km) + '</b> ' + esc(T.km) + '</span><span>' + esc(T.approx) + ' <b>' + fmt(d.hm) + '</b> ' + esc(T.hm) + '</span></div></div>' +
          '<div class="ct-daycard__map">' + mapSVG(D, Z, dayPath(D, d.from_km, d.to_km), over, tpl(T.map_label, { n: d.day })) + '</div></div>' + timeLine(P.tier) + '</div>';
      });
      items += goalItem(st.days[n - 1].to, T.goal_note);
      return sumStrip(tierName(P.tier), tpl(T.sum_from, { town: st.start.name }), [[n, n > 1 ? T.days : T.day], [esc(T.approx) + ' ' + fmt(D.meta.shop.km), T.km], [fmt(D.meta.shop.hm), T.hm]]) +
        tlWrap(items) + planCta(k, P.tier, tierDays(P.tier) + ' ' + tpl(T.sum_from, { town: st.start.name }));
    }
    function basecampHTML(k, sp) {
      var c = CH[k], D = c.D, bc = basecampPlan(k);
      if (!bc) return stagesHTML(k, sp);
      var hotel = bc.hotel, m = bc.s, Z = fitPts(D.pts, 40);
      var h = sumStrip(tierName('silber'), tpl(T.sum_basecamp, { town: hotel.town }), [[2, T.days], [2, T.nights_same]]);
      if (sp.name !== hotel.town) {
        h += '<div class="ct-pl-note" data-ms-rv>' + ICON.info + '<div><span>' + tpl(esc(T.basecamp_note), { town: '<b>' + esc(hotel.town) + '</b>', start: '<b>' + esc(sp.name) + '</b>' }) + '</span>' +
          '<div class="ct-pl-note__btns"><button type="button" class="ct-btn ct-btn--sm" data-set-variant="etappe">' + esc(T.basecamp_alt) + '</button></div></div></div>';
      }
      var hotelKm = hotel.km, turnKm = m.turn_km != null ? r1(m.turn_km * c.R.fk) : null;
      if (turnKm == null && m.turn_place) { var tp0 = D.places.filter(function (p) { return p.name === m.turn_place; })[0]; if (tp0) turnKm = tp0.km; }
      var tp = turnKm != null ? (function (p) { return [p[2], p[3]]; })(pointAt(D, turnKm)) : null;
      var xfer = tp ? '<path class="ct-map__xfer" d="M' + Z.X(hotel.x) + ' ' + Z.Y(hotel.y) + 'L' + Z.X(tp[0]) + ' ' + Z.Y(tp[1]) + '"/>' : '';
      var over = xfer + pt(Z, [hotel.x, hotel.y], '', 9, 'var(--ct-c)') + (tp ? pt(Z, tp, 'ct-map__split', 7, '#fff') : '');
      var turn = (m.split ? T.split_point : T.turn) + ' ' + esc(m.turn_place || '');
      var tr = m.transfer_km != null ? tpl(T.transfer, { km: fmt(m.transfer_km), point: m.split ? T.split_point : T.turn_point }) : T.transfer_plain;
      h += tlWrap(
        '<div class="ct-tl__item" data-ms-rv><span class="ct-tl__dot"></span><h3>' + esc(T.night_both) + ' <span>· ' + esc(hotel.town) + '</span></h3><p class="ct-tl__note">' + esc(T.note_same) + '</p>' + stayCards(k, hotel.town, hotel) + '</div>' +
        '<div class="ct-tl__item" data-ms-rv>' + dayCard(D, Z, 1, esc(hotel.town), turn, m.day1_km, m.day1_hm, hotelKm, turnKm, over) + '<div class="ct-transfer">' + ICON.bed + '<span>' + esc(tr) + '</span></div></div>' +
        '<div class="ct-tl__item" data-ms-rv>' + dayCard(D, Z, 2, turn, esc(hotel.town), m.day2_km, m.day2_hm, turnKm, hotelKm, over) + '<p class="ct-tl__time">' + esc(T.values_note) + '</p></div>' +
        goalItem(hotel.town, T.goal_note));
      return h + planCta(k, 'silber', T.variant_basecamp + ' ' + hotel.name);
    }

    /* Die Linie links: eine Route, die sich durch die Stationen schlaengelt und beim Scrollen mitwaechst */
    var tlState = null;
    function routeLine(scope) {
      var tl = q('.ct-tl', scope); tlState = null;
      if (!tl) return;
      var svg = q('[data-tlsvg]', tl), base = q('[data-tlbase]', tl), on = q('[data-tlon]', tl), clip = q('[data-tlrect]', tl);
      if (!svg || !on) return;
      var S = { tl: tl, builtH: 0, y0: 0, y1: 0 };
      var build = function () {
        var pts = [];
        qa('.ct-tl__item', tl).forEach(function (it) { var d = it.querySelector('.ct-tl__dot'); if (d) pts.push({ el: d, x: it.offsetLeft + d.offsetLeft + d.offsetWidth / 2, y: it.offsetTop + d.offsetTop + d.offsetHeight / 2 }); });
        if (pts.length < 2) return;
        var w = Math.round(tl.clientWidth), h = Math.round(tl.offsetHeight), dotW = pts[0].el.offsetWidth || 23;
        var amp = Math.max(5, Math.min(10, pts[0].x * 0.8)), mid = Math.max(pts[0].x, amp + dotW / 2), y0 = pts[0].y, y1 = pts[pts.length - 1].y;
        var period = Math.max(360, (y1 - y0) / Math.max(1, Math.round((y1 - y0) / 560)));
        var wave = function (y) { return mid + amp * Math.sin((y - y0) / period * Math.PI * 2); };
        pts.forEach(function (p) { p.el.style.setProperty('--dx', (wave(p.y) - p.x).toFixed(1) + 'px'); });
        var d = 'M' + wave(y0).toFixed(1) + ' ' + y0.toFixed(1);
        for (var y = y0 + 8; y < y1; y += 8) d += 'L' + wave(y).toFixed(1) + ' ' + y.toFixed(1);
        d += 'L' + wave(y1).toFixed(1) + ' ' + y1.toFixed(1);
        svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h); svg.setAttribute('width', w); svg.setAttribute('height', h);
        base.setAttribute('d', d); on.setAttribute('d', d);
        if (clip) { clip.setAttribute('x', '0'); clip.setAttribute('y', '0'); clip.setAttribute('width', w + 40); }
        S.y0 = y0; S.y1 = y1; S.builtH = h;
      };
      S.scene = function () {
        if (!tl.isConnected) return;
        if (Math.abs(tl.offsetHeight - S.builtH) > 2) build();
        var M = MC.measures || {}, top0 = (M.headerH || 0) + (M.tabsH || 0), vh = Math.max(200, window.innerHeight - top0 - (M.fcH || 0));
        var r = tl.getBoundingClientRect(), hoehe = Math.max(1, r.height), oben = r.top + window.scrollY;
        var maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        var marke = window.scrollY + top0 + vh * 0.75, markeMax = maxScroll + top0 + vh * 0.75;
        var jetzt = (marke - oben) / hoehe, amEnde = (markeMax - oben) / hoehe;
        var p = reduce() ? 1 : Math.max(0, Math.min(1, amEnde < 1 ? jetzt / Math.max(0.0001, amEnde) : jetzt));
        if (clip) clip.setAttribute('height', (S.y0 + p * (S.y1 - S.y0) + 3).toFixed(1));
      };
      build(); S.scene();
      if (window.ResizeObserver) { S.ro = new ResizeObserver(function () { build(); S.scene(); }); S.ro.observe(tl); }
      tlState = S;
    }
    var onScroll = function () { if (tlState && entered) tlState.scene(); };
    window.addEventListener('scroll', onScroll, { passive: true });
    disposers.push(function () { window.removeEventListener('scroll', onScroll); if (tlState && tlState.ro) tlState.ro.disconnect(); });

    /* ---------- Editor-Diagnose: welche Hotels der Planer sieht ---------- */
    function renderDiag() {
      var c = CH[P.ch], why = T.diag_why || {};
      var bad = c.skipped.map(function (s) { return s.hotel.name + ' (' + (why[s.why] || s.why) + ')'; });
      diag.textContent = tpl(T.diag, { label: P.ch.toUpperCase(), n: c.hotels.length, towns: c.towns.map(function (p) { return p.name; }).join(', ') }) + (bad.length ? ' ' + tpl(T.diag_bad, { list: bad.join('; ') }) : '');
    }

    /* ---------- Kaufbox: lesen und schreiben ---------- */
    function toast(msg) {
      var t = q('[data-ms-plan-toast]', panel);
      if (!t) { t = document.createElement('div'); t.className = 'ct-toast'; t.setAttribute('data-ms-plan-toast', ''); t.setAttribute('role', 'status'); panel.appendChild(t); }
      t.textContent = msg; t.classList.add('is-on');
      clearTimeout(toastT); toastT = setTimeout(function () { t.classList.remove('is-on'); }, 2600);
    }
    function takeTier(tier) {
      var input = tierRadios().filter(function (r) { return tierOfValue(r.value) === tier; })[0];
      var fs = input && input.closest('fieldset');
      var box = fs || q('.ms-config') || q('[data-ms-tier]');
      if (!input || input.disabled || (fs && fs.classList.contains('ms-locked'))) {
        toast(T.locked_toast);
      } else {
        if (!input.checked) input.click();
        toast(tpl(T.took_toast, { tier: tierName(tier) }));
      }
      if (box) box.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' });
    }
    var onPanelClick = function (e) {
      var v = e.target.closest('[data-pick-variant]');
      if (v && !v.disabled) { if (v.getAttribute('data-pick-variant') === P.variant) return; P.variant = v.getAttribute('data-pick-variant'); renderPick(); swapPlan(); persist(); return; }
      var sv = e.target.closest('[data-set-variant]');
      if (sv) { P.variant = sv.getAttribute('data-set-variant'); renderPick(); swapPlan(); persist(); return; }
      var go = e.target.closest('.ct-stay__go');
      if (go) {
        // Rueckweg fuer die Hotelseite merken: welche Plan-URL, zu welchem Hotel
        persist();
        try { sessionStorage.setItem('ms-plan-back', JSON.stringify({ url: planBackUrl(), hotel: new URL(go.href, location.href).pathname, t: Date.now() })); } catch (err) {}
      }
      var take = e.target.closest('[data-ms-plan-take]');
      if (take) { takeTier(take.getAttribute('data-ms-plan-take')); }
    };
    panel.addEventListener('click', onPanelClick);
    var onSelection = function (e) {
      var opts = e.detail && e.detail.options, t = opts && opts[1] ? tierOfValue(opts[1]) : null;
      if (!t || t === P.tier || !rendered) return;
      P.tier = t; syncStart(); autoVariant(); renderPick(true); swapPlan(); persist(); tierChanged();
    };
    document.addEventListener('ms:selection-change', onSelection);
    // Stufe aus der Box "Wann fahren?" (gleiche Kopplung wie die Kaufbox)
    var onWannTier = function (e) { var t = e.detail && tierOfValue(e.detail.tier); if (t && t !== P.tier && rendered) onSelect('tier', t); };
    document.addEventListener('ms:wann-tier', onWannTier);
    disposers.push(function () { panel.removeEventListener('click', onPanelClick); document.removeEventListener('ms:selection-change', onSelection); document.removeEventListener('ms:wann-tier', onWannTier); });

    /* ---------- Aufbau ---------- */
    var started = null;
    function start() {
      if (started) return started;
      panel.classList.add('is-loading');
      started = ensure(ctx.key).then(function () {
        var bt = buyboxTier(); if (bt && !new URLSearchParams(location.search).get('ms_tier')) P.tier = bt;
        // Ohne Variante in der URL gilt die Vorgabe des Startorts; eine gewaehlte Variante bleibt
        syncStart();
        if (!new URLSearchParams(location.search).get('ms_variant')) autoVariant(); else if (P.tier !== 'silber') P.variant = 'etappe';
        panel.classList.remove('is-loading');
        renderPick(); renderPlan(); tierChanged();
        // Andere Challenge aus der URL (Rueckweg vom Hotel)
        var chU = new URLSearchParams(location.search).get('ms_ch');
        if (chU && chU !== ctx.key && CFG.routes && CFG.routes[chU]) switchChallenge(chU);
      }).catch(function (e) { panel.classList.remove('is-loading'); if (window.console) console.error('ms-challenge-planner', e); });
      return started;
    }
    // Sofort vorbereiten, wenn der Tab schon aktiv ist (Hash oder URL-Parameter), sonst beim Betreten
    if (panel.classList.contains('is-active') || /[?&]ms_(tier|start|variant|ch)=/.test(location.search)) start();

    return {
      enter: function () { entered = true; start().then(function () { if (tlState) tlState.scene(); }); },
      leave: function () { entered = false; if (DD.open) ddClose(true); },
      scene: function () { if (tlState) tlState.scene(); },
      destroy: function () { if (DD.open) ddClose(true); disposers.forEach(function (f) { try { f(); } catch (e) {} }); }
    };
  });

  // Laeuft die Laufzeit schon (sie startet sofort, dieses Skript kommt spaeter im DOM), legt
  // register() das Modul selbst an und betritt es, wenn der Tab aktiv ist.
})();
